import { ActionContext, ActionTree } from 'vuex';
import { RootState } from '@/interface/store/';
import * as s_Interface from '@/interface/store/inventory';
import * as c_Interface from '@/interface/components/inventory';
import { File, FilePayload, ExportData } from '@/interface/components';
import * as API from '@/api/inventory';

export const actions: ActionTree<s_Interface.Inventory, RootState> = {
  async getInventory(
    { commit }: ActionContext<s_Interface.Inventory, RootState>,
    {
      groupId,
      attributes,
      limit,
      offset,
      order,
      include,
      filters,
      query,
    }: {
      groupId: string;
      attributes: string;
      limit: number;
      offset: number;
      order: string;
      include: string;
      filters: s_Interface.InventoryFilter;
      query: string;
    }
  ) {
    try {
      const { data } = await API.getInventory(
        groupId,
        attributes,
        limit,
        offset,
        order,
        include,
        filters,
        query
      );
      commit('SET_INVENTORY', data);
      commit('UPDATE_QUERY_PARAMS', {
        limit,
        offset,
        order,
      });
    } catch (e) {
      throw e;
    }
  },
  async getMobileInventory(
    { commit }: ActionContext<s_Interface.Inventory, RootState>,
    {
      groupId,
      attributes,
      limit,
      offset,
      order,
      include,
      filters,
      query,
    }: {
      groupId: string;
      attributes: string;
      limit: number;
      offset: number;
      order: string;
      include: string;
      filters: s_Interface.InventoryFilter;
      query: string;
    }
  ) {
    try {
      const { data } = await API.getInventory(
        groupId,
        attributes,
        limit,
        offset,
        order,
        include,
        filters,
        query
      );
      commit('SET_MOBILE_INVENTORY', data);
      commit('UPDATE_QUERY_PARAMS', {
        limit,
        offset,
        order,
      });
    } catch (e) {
      throw e;
    }
  },
  async attachmentInventorySignUrl({ commit }) {},
  async setInventoryItem(
    { commit },
    {
      groupId,
      payload,
      files,
    }: { groupId: string; payload: c_Interface.InventoryItemPayload; files: Array<File> }
  ) {
    try {
      const { data } = await API.setInventory(groupId, payload);
      const inventoryId = data.id;
      const filesPayload = files.map((file: File) => {
        return {
          Bucket: 'images.labsby.com',
          Key: `groups/${groupId}/inventory/${inventoryId}/attachments/${file.name}`,
        };
      });
      const result = await API.attachmentsInventorySignUrl(groupId, inventoryId, filesPayload);
      result.data.url.forEach((url: string, i: number) => {
        API.attachmentsInventoryUpload(url, files[i]);
      });
      const insertDataPayload = files.map((file: File) => {
        return {
          url: `https://images.labsby.com/groups/${groupId}/inventory/${inventoryId}/attachments/${file.name}`,
          fileName: file.name,
        };
      });
      await API.setAttachments(groupId, inventoryId, insertDataPayload);
    } catch (e) {
      throw e;
    }
  },
  async setAttachment(
    { commit },
    { groupId, inventoryId, files }: { groupId: string; inventoryId: string; files: Array<File> }
  ) {
    try {
      const filesPayload = files.map((file: File) => {
        return {
          Bucket: 'images.labsby.com',
          Key: `groups/${groupId}/inventory/${inventoryId}/attachments/${file.name}`,
        };
      });
      const result: any = await API.attachmentsInventorySignUrl(groupId, inventoryId, filesPayload);
      result.data.url.forEach((url: string, i: number) => {
        API.attachmentsInventoryUpload(url, files[i]);
      });
      const insertDataPayload = files.map((file: File) => {
        return {
          url: `https://images.labsby.com/groups/${groupId}/inventory/${inventoryId}/attachments/${file.name}`,
          fileName: file.name,
        };
      });
      const { data } = await API.setAttachments(groupId, inventoryId, insertDataPayload);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async getAttachments(
    { commit }: ActionContext<s_Interface.Inventory, RootState>,
    { groupId, inventoryId }: { groupId: string; inventoryId: string }
  ) {
    try {
      const { data } = await API.getAttachments(groupId, inventoryId);
      commit('SET_INVENTORY_ATTACHMENTS', data);
    } catch (e) {
      throw e;
    }
  },
  async deleteAttachment(
    { commit },
    {
      groupId,
      inventoryId,
      attachmentId,
    }: { groupId: string; inventoryId: string; attachmentId: string }
  ) {
    try {
      await API.deleteAttachment(groupId, inventoryId, attachmentId);
    } catch (e) {
      throw e;
    }
  },
  async bulkSetInventory(
    { commit },
    { groupId, payload }: { groupId: string; payload: s_Interface.InventoryItem }
  ) {
    try {
      const { data } = await API.setInventoryBulk(groupId, payload);
    } catch (e) {
      throw e;
    }
  },
  async getInventoryItem(
    { commit }: ActionContext<s_Interface.Inventory, RootState>,
    { groupId, inventoryId }: { groupId: string; inventoryId: string }
  ) {
    try {
      const { data } = await API.getInventoryItem(groupId, inventoryId);
      commit('SET_INVENTORY_ITEM', data);
    } catch (e) {
      throw e;
    }
  },
  async updateInventoryItem(
    { commit },
    {
      groupId,
      inventoryId,
      payload,
    }: { groupId: string; inventoryId: string; payload: c_Interface.InventoryItemPayload }
  ) {
    try {
      const { data } = await API.updateInventory(groupId, inventoryId, payload);
    } catch (e) {
      throw e;
    }
  },
  async bulkUpdateInventoryItem(
    { commit },
    { groupId, payload }: { groupId: string; payload: s_Interface.InventoryItem }
  ) {
    try {
      const { data } = await API.bulkUpdateInventory(groupId, payload);
    } catch (e) {
      throw e;
    }
  },
  async deleteInventory(
    { commit },
    { groupId, inventoryId }: { groupId: string; inventoryId: string }
  ) {
    try {
      const { data } = await API.deleteInventory(groupId, inventoryId);
    } catch (e) {
      throw e;
    }
  },
  async bulkDeleteInventory(
    { commit },
    { groupId, payload }: { groupId: string; payload: Array<string> }
  ) {
    try {
      const { data } = await API.bulkDeleteInventory(groupId, payload);
    } catch (e) {
      throw e;
    }
  },
  async getExportInventory(
    { commit },
    { groupId, startAt, endAt }: { groupId: string; startAt: string; endAt: string }
  ): Promise<ExportData> {
    try {
      const { data } = await API.exportInventory(groupId, startAt, endAt);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async getTemplateInventory({ commit }, groupId: string) {
    try {
      const { data } = await API.getTemplateInventory(groupId);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async signUrlInventoryImport({ commit }, { groupId, file }: { groupId: string; file: File }) {
    try {
      const payload = {
        Key: `groups/${groupId}/imports/${file.name}`,
        Bucket: `images.labsby.com`,
      };
      const { data } = await API.signUrlInventoryImport(groupId, payload);
      const result = await API.inventoryImport(data.url, file);
    } catch (e) {
      throw e;
    }
  },
  async inventoryUpload({ commit }, uploadPayload: { groupId: string; payload: FilePayload }) {
    try {
      const { data } = await API.importUpload(uploadPayload.groupId, uploadPayload.payload);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async getInventoryHistories({ commit }, { groupId, inventoryId }) {
    try {
      const { data } = await API.getInventoryHistories(groupId, inventoryId);
      commit('SET_INVENTORY_HISTORIES', data);
      return data;
    } catch (e) {
      throw e;
    }
  },
  async getQueryParams({ commit }, { groupId }: { groupId: string }) {
    try {
      const { data } = await API.getQueryParams(groupId);
      commit('SET_QUERY_PARAMS', data);
    } catch (e) {
      throw e;
    }
  },
  async updateQueryParams({ commit }, { groupId, payload }) {
    try {
      await API.updateQueryParams(groupId, payload);
      commit('UPDATE_QUERY_PARAMS', payload);
    } catch (e) {
      throw e;
    }
  },
};
